import { grey } from "@mui/material/colors";
import {
  alpha,
  createTheme as muiCreateTheme,
  darken,
  lighten
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
export * from "@mui/material/styles";
export * from "@mui/utils";
const defaultFontFamily = [
  '"Source Sans Pro"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"'
].join(",");
const primaryMain = "#4671A9";
const secondaryMain = grey[800];
const errorMain = "#D32E2F";
const warningMain = "#D18300";
const infoMain = "#1976D2";
const successMain = "#2E7D33";
const neutralMain = grey[900];
const customShadows = {
  0: "none",
  // or '0px 0px 0px 0px rgba(0, 0, 0, 0.00)'
  1: "0px 0px 3px 0px rgba(5, 10, 15, 0.15), 0px 2px 3px 0px rgba(5, 10, 15, 0.09), 0px 1px 1px 0px rgba(0, 0, 0, 0.07)",
  3: "0px 2px 10px -1px rgba(5, 10, 15, 0.15), 0px 4px 5px 0px rgba(5, 10, 15, 0.09), 0px 2px 3px -1px rgba(0, 0, 0, 0.07)",
  6: "0px 5px 16px -1px rgba(5, 10, 15, 0.15), 0px 7px 8px 0px rgba(5, 10, 15, 0.09), 0px 1px 18px 0px rgba(0, 0, 0, 0.07)",
  8: "0px 7px 20px -2px rgba(5, 10, 15, 0.15), 0px 9px 9px 0px rgba(5, 10, 15, 0.09), 0px 3px 14px 2px rgba(0, 0, 0, 0.07)",
  12: "0px 11px 28px -3px rgba(5, 10, 15, 0.15), 0px 13px 14px 0px rgba(5, 10, 15, 0.09), 0px 5px 22px 4px rgba(0, 0, 0, 0.07)"
};
const baseTheme = muiCreateTheme({
  palette: {
    contrastThreshold: 4.5,
    primary: {
      main: primaryMain
    },
    secondary: {
      main: secondaryMain
    },
    success: {
      main: successMain
    },
    info: {
      main: infoMain
    },
    warning: {
      main: warningMain
    },
    error: {
      main: errorMain
    }
  }
});
const themeWithPrimitives = muiCreateTheme(
  deepmerge(baseTheme, {
    breakpoints: {
      values: {
        messageCard: 600
      }
    },
    palette: {
      background: {
        danger: alpha(baseTheme.palette.error.main, 0.12),
        info: alpha(baseTheme.palette.info.main, 0.12),
        secondary: baseTheme.palette.grey[100],
        success: alpha(baseTheme.palette.success.main, 0.12),
        warning: alpha(baseTheme.palette.warning.main, 0.12)
      },
      charts: {
        colors: [
          "#173A5E",
          // deepBlue
          "#00A3A0",
          // tealWave
          "#C91B63",
          // raspberryPunch
          "#EF5350",
          // coralBlaze
          "#FFA726",
          // mangoGlow
          "#B800D8",
          // electricPurple
          "#60009B",
          // royalIndigo
          "#2E96FF",
          // skyFlash
          "#2731C8",
          // cobaltStorm
          "#03008D"
          // midnightCore
        ]
      },
      default: {
        main: baseTheme.palette.grey[500],
        light: baseTheme.palette.grey[400],
        dark: baseTheme.palette.grey[900],
        contrastText: baseTheme.palette.common.white
      },
      // The primary palette colors are hardcoded to match the Figma design palette (not auto-generated).
      // This ensures exact visual alignment with design specs.
      primary: {
        50: "#e7f3fa",
        100: "#c4e1f4",
        200: "#a3d0ee",
        300: "#85bde5",
        400: "#71aee0",
        500: "#62a1db",
        600: "#5993ce",
        700: "#4e81bb",
        800: "#4671a9",
        900: "#385488"
      },
      // The white palette color is specifically used for the AppBar component
      white: {
        main: baseTheme.palette.common.white
      },
      neutral: {
        main: neutralMain,
        light: lighten(neutralMain, 0.2),
        dark: darken(neutralMain, 0.3),
        contrastText: baseTheme.palette.common.white
      }
    },
    // mobile first responsive type ramp
    typography: {
      fontFamily: defaultFontFamily,
      fontWeightMedium: 600,
      fontWeightInlineBold: 600,
      h1: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1.5rem",
        lineHeight: 1.2,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.875rem",
          lineHeight: 1.2
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "2rem",
          lineHeight: 1.2
        }
      },
      h2: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1.375rem",
        lineHeight: 1.2,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.625rem",
          lineHeight: 1.2
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "1.75rem",
          lineHeight: 1.2
        }
      },
      h3: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1.25rem",
        lineHeight: 1.2,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.375rem",
          lineHeight: 1.2
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "1.5rem",
          lineHeight: 1.2
        }
      },
      h4: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1.125rem",
        lineHeight: 1.2,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.25rem",
          lineHeight: 1.2
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "1.25rem",
          lineHeight: 1.2
        }
      },
      h5: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1rem",
        lineHeight: 1.5,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.125rem",
          lineHeight: 1.5
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "1.125rem",
          lineHeight: 1.5
        }
      },
      h6: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        letterSpacing: "0px",
        fontSize: "1rem",
        lineHeight: 1.5
      },
      subtitle1: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        letterSpacing: "0px"
      },
      subtitle2: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.313rem",
        letterSpacing: "0px"
      },
      body1: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        letterSpacing: "0px",
        fontSize: "1rem",
        lineHeight: 1.5,
        [baseTheme.breakpoints.up("sm")]: {
          fontSize: "1.125rem",
          lineHeight: 1.5
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: "1.125rem",
          lineHeight: 1.5
        }
      },
      body2: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        letterSpacing: "0px",
        fontSize: "1rem",
        lineHeight: 1.2
      },
      caption: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.313rem",
        letterSpacing: "0px",
        textTransform: "uppercase"
      },
      button: {
        fontFamily: defaultFontFamily,
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        letterSpacing: "0.5px",
        textTransform: "none"
      },
      overline: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.313rem",
        letterSpacing: "0px",
        textTransform: "none"
      }
    },
    sizes: {
      avatar: {
        "24": { width: "24px", height: "24px", fontSize: "14px" },
        "32": { width: "32px", height: "32px", fontSize: "16px" },
        "40": { width: "40px", height: "40px", fontSize: "20px" },
        "48": { width: "48px", height: "48px", fontSize: "24px" },
        "56": { width: "56px", height: "56px", fontSize: "28px" },
        "72": { width: "72px", height: "72px", fontSize: "36px" },
        "96": { width: "96px", height: "96px", fontSize: "48px" },
        "128": { width: "128px", height: "128px", fontSize: "64px" }
      }
    },
    shadows: [
      customShadows[0],
      ...Array(2).fill(customShadows[1]),
      ...Array(3).fill(customShadows[3]),
      ...Array(2).fill(customShadows[6]),
      ...Array(4).fill(customShadows[8]),
      ...Array(13).fill(customShadows[12])
    ]
  })
);
const theme = muiCreateTheme(
  deepmerge(
    {
      // Add theme customizations here
      components: {
        MuiAccordion: {
          defaultProps: {
            elevation: 1
          }
        },
        MuiAccordionActions: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightMedium,
              fontSize: currentTheme.typography.body1.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing
            })
          }
        },
        MuiAccordionDetails: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightRegular,
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing
            })
          }
        },
        MuiAccordionSummary: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightBold,
              fontSize: currentTheme.typography.body1.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing,
              "&:is(:focus-visible, .Mui-focusVisible)": {
                backgroundColor: "transparent",
                outline: "3px solid",
                outlineColor: currentTheme.palette.neutral.main,
                outlineOffset: "-3px",
                borderRadius: currentTheme.shape.borderRadius
              }
            }),
            content: ({ theme: currentTheme }) => ({
              flexDirection: "column",
              gap: currentTheme.spacing(0.5)
            }),
            primaryText: ({ theme: currentTheme }) => ({
              fontWeight: currentTheme.typography.fontWeightMedium,
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body2.lineHeight,
              letterSpacing: currentTheme.typography.body2.letterSpacing
            })
          }
        },
        MuiAlert: {
          defaultProps: {
            variant: "filled",
            severity: "neutral"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              alignItems: "flex-start",
              display: "grid",
              gridTemplateAreas: '"icon message action"',
              gridTemplateColumns: "auto 1fr auto",
              padding: `${currentTheme.spacing(1.5)} ${currentTheme.spacing(
                3
              )}`
            }),
            outlined: ({ theme: currentTheme, ownerState }) => {
              const severity = ownerState.severity || "neutral";
              return {
                color: currentTheme.palette.text.primary,
                backgroundColor: currentTheme.palette.common.white,
                borderColor: currentTheme.palette[severity].main,
                "& .MuiButton-root:not(:disabled, .disabled)": {
                  color: currentTheme.palette[severity].main
                }
              };
            },
            icon: ({ theme: currentTheme }) => ({
              gridArea: "icon",
              fontSize: currentTheme.typography.h3.fontSize,
              alignSelf: "flex-start",
              marginTop: "2px"
            }),
            message: ({ theme: currentTheme }) => ({
              gridArea: "message",
              display: "flex",
              flexFlow: "column nowrap",
              gap: 0,
              fontSize: currentTheme.typography.body2.fontSize,
              fontWeight: currentTheme.typography.fontWeightRegular
            }),
            action: ({ theme: currentTheme }) => ({
              gridArea: "action",
              alignItems: "center",
              padding: currentTheme.spacing(0, 0, 0, 2),
              columnGap: currentTheme.spacing(1.5),
              "&:not(:disabled, .disabled)": {
                color: "inherit"
              },
              "& .MuiIconButton-root": {
                "&:not(:disabled, .disabled)": {
                  color: "inherit"
                },
                "&:hover, &.hover": {
                  backgroundColor: "color-mix(in srgb, currentColor 10%, transparent)",
                  borderColor: "color-mix(in srgb, currentColor 10%, transparent)"
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)",
                  borderColor: "currentColor"
                },
                "&:focus-visible:not(:active), &.focus": {
                  borderColor: "currentColor",
                  boxShadow: `0 0 0 1px currentColor`
                }
              },
              "& .MuiButton-root": {
                "&:not(:disabled, .disabled)": {
                  color: "inherit"
                },
                "&:hover, &.hover": {
                  backgroundColor: "color-mix(in srgb, currentColor 10%, transparent)",
                  borderColor: "color-mix(in srgb, currentColor 10%, transparent)"
                },
                "&:focus, &.focus": {
                  borderColor: "currentColor",
                  boxShadow: `0 0 0 1px currentColor`
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)",
                  borderColor: "currentColor"
                },
                "&:disabled, &.disabled": {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  boxShadow: "none"
                }
              }
            })
          }
        },
        MuiAlertTitle: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              margin: 0,
              fontWeight: currentTheme.typography.fontWeightMedium,
              fontSize: currentTheme.typography.body2.fontSize
            })
          }
        },
        MuiAutocomplete: {
          defaultProps: {
            size: "medium"
          },
          styleOverrides: {
            root: ({ ownerState }) => ({
              width: ownerState.fullWidth ? "100%" : "fit-content",
              minWidth: "16rem",
              "& .MuiAutocomplete-endAdornment": {
                right: 12
              },
              "& .MuiFormControl-root": {
                width: "100%"
              },
              "& .MuiFormControl-root .MuiFormLabel-root": {
                marginBottom: "4px"
              },
              "& .MuiFormControl-root .MuiFormHelperText-root": {
                marginTop: "2px"
              }
            }),
            popupIndicator: ({ theme: currentTheme, ownerState }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.action.active,
                ...ownerState.size === "small" && {
                  padding: currentTheme.spacing(0.25, 0.25),
                  minWidth: currentTheme.spacing(4),
                  minHeight: currentTheme.spacing(4)
                },
                "&:hover, &:focus, &.hover, &.focus": {
                  backgroundColor: `color-mix(in srgb, currentColor 5%, ${currentTheme.palette.common.white})`
                }
              }
            }),
            clearIndicator: ({ theme: currentTheme, ownerState }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.action.active,
                backgroundColor: currentTheme.palette.common.white,
                ...ownerState.size === "small" && {
                  padding: currentTheme.spacing(0.25, 0.25),
                  minWidth: currentTheme.spacing(4),
                  minHeight: currentTheme.spacing(4)
                },
                "&:hover, &:focus, &.hover, &.focus": {
                  backgroundColor: `color-mix(in srgb, currentColor 5%, ${currentTheme.palette.common.white})`
                }
              }
            }),
            tag: () => ({
              "&:not(.MuiChip-root)": {
                marginLeft: "8px"
              }
            }),
            option: ({ theme: currentTheme }) => ({
              '&[aria-selected="true"]': {
                fontWeight: currentTheme.typography.fontWeightBold
              },
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body2.lineHeight
            }),
            input: () => ({
              padding: 0,
              margin: 8,
              "&.MuiInputBase-inputSizeSmall": {
                padding: 0,
                margin: 4
              }
            }),
            inputRoot: ({ ownerState, theme: currentTheme }) => ({
              paddingTop: ownerState.size === "medium" ? 8 : 4,
              paddingBottom: ownerState.size === "medium" ? 8 : 4,
              "&.MuiAutocomplete-inputRoot.MuiInputBase-adornedEnd": {
                paddingRight: currentTheme.spacing(10)
              }
            })
          }
        },
        MuiAvatar: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              backgroundColor: "#e3f2fd",
              color: currentTheme.palette.primary.dark,
              fontWeight: 700
            })
          }
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: "rgba(0,0,0,0.32)"
            }
          }
        },
        MuiBadge: {
          defaultProps: {
            color: "default"
          },
          variants: [
            {
              props: { color: "default" },
              style: ({ theme: currentTheme }) => ({
                "& .MuiBadge-badge": {
                  backgroundColor: currentTheme.palette.secondary.main
                }
              })
            }
          ],
          styleOverrides: {
            badge: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.subtitle2.fontSize,
              fontWeight: currentTheme.typography.fontWeightMedium
            }),
            // Apply spacing to all badge variants
            root: {
              "& .MuiBadge-badge": {
                top: "-2px",
                right: "-2px"
              },
              // Top-left positioning
              "& .MuiBadge-anchorOriginTopLeft": {
                top: "-2px",
                left: "-2px"
              },
              // Bottom-right positioning
              "& .MuiBadge-anchorOriginBottomRight": {
                bottom: "-2px",
                right: "-2px",
                top: "auto"
              },
              // Bottom-left positioning
              "& .MuiBadge-anchorOriginBottomLeft": {
                bottom: "-2px",
                left: "-2px",
                top: "auto"
              }
            }
          }
        },
        MuiBottomNavigationAction: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.text.secondary,
              "&.Mui-disabled": {
                color: currentTheme.palette.text.disabled
              },
              "&:focus-visible, &.focus, &:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2),
                "&.Mui-selected": {
                  backgroundColor: alpha(
                    currentTheme.palette.primary.main,
                    0.1
                  )
                }
              }
            }),
            label: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.pxToRem(14)
            })
          }
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
            disableFocusRipple: true,
            disableRipple: true,
            disableTouchRipple: true,
            size: "medium"
          },
          variants: [
            {
              props: { size: "small" },
              style: ({ theme: currentTheme }) => ({
                padding: currentTheme.spacing(0.25, 1.25),
                minHeight: "32px",
                fontSize: "1rem",
                "&.MuiButton-text": {
                  padding: currentTheme.spacing(0.25, 1)
                }
              })
            },
            {
              props: { size: "medium" },
              style: ({ theme: currentTheme }) => ({
                padding: currentTheme.spacing(1, 2),
                minHeight: "44px",
                fontSize: "1rem",
                "&.MuiButton-text": {
                  padding: currentTheme.spacing(1, 1.5)
                }
              })
            },
            {
              props: { size: "large" },
              style: ({ theme: currentTheme }) => ({
                padding: currentTheme.spacing(1, 2),
                minHeight: "44px",
                fontSize: "1rem",
                "&.MuiButton-text": {
                  padding: currentTheme.spacing(1, 1.5)
                }
              })
            },
            {
              props: { color: "error" },
              style: ({ theme: currentTheme }) => ({
                "&.MuiButton-contained": {
                  "&:not(:disabled, .disabled)": {
                    backgroundColor: currentTheme.palette.error.main,
                    color: currentTheme.palette.error.contrastText,
                    borderColor: currentTheme.palette.error.main,
                    "&:hover, &:focus, &.hover, &.focus": {
                      backgroundColor: darken(
                        currentTheme.palette.error.main,
                        0.2
                      ),
                      borderColor: darken(currentTheme.palette.error.main, 0.2)
                    },
                    "&:focus:not(:focus-visible):not(:hover)": {
                      boxShadow: "none",
                      borderColor: currentTheme.palette.error.main,
                      backgroundColor: currentTheme.palette.error.main
                    },
                    "&:focus-visible, &.focus": {
                      borderColor: currentTheme.palette.common.white,
                      boxShadow: `0 0 0 1px ${currentTheme.palette.common.white}, 0 0 0 3px ${darken(
                        currentTheme.palette.error.main,
                        0.2
                      )}`
                    },
                    "&:active, &.active": {
                      boxShadow: "none",
                      backgroundColor: darken(
                        currentTheme.palette.error.main,
                        0.4
                      ),
                      borderColor: darken(currentTheme.palette.error.main, 0.4)
                    }
                  }
                },
                "&.MuiButton-outlined": {
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.error.main,
                    borderColor: currentTheme.palette.error.main,
                    backgroundColor: currentTheme.palette.common.white,
                    "&:hover, &.hover": {
                      backgroundColor: `color-mix(in srgb, currentColor 10%, ${currentTheme.palette.common.white})`
                    },
                    "&:focus:not(:focus-visible)": {
                      outline: "none",
                      boxShadow: "none"
                    },
                    "&:focus-visible, &.focus": {
                      borderColor: currentTheme.palette.grey[900],
                      boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                    },
                    "&:active, &.active": {
                      backgroundColor: `color-mix(in srgb, currentColor 20%, ${currentTheme.palette.common.white})`,
                      borderColor: currentTheme.palette.error.dark,
                      boxShadow: "none"
                    }
                  }
                },
                "&.MuiButton-text": {
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.error.main,
                    "&:hover, &.hover": {
                      backgroundColor: alpha(
                        currentTheme.palette.error.main,
                        0.1
                      )
                    },
                    "&:focus:not(:focus-visible)": {
                      outline: "none",
                      boxShadow: "none"
                    },
                    "&:focus-visible:not(:active), &.focus": {
                      borderColor: currentTheme.palette.grey[900],
                      boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                    },
                    "&:active, &.active": {
                      boxShadow: "none",
                      backgroundColor: alpha(
                        currentTheme.palette.error.main,
                        0.2
                      )
                    }
                  }
                }
              })
            }
          ],
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              borderColor: "transparent",
              borderStyle: "solid",
              borderWidth: "1px",
              "&:disabled, &.disabled": {
                color: currentTheme.palette.text.disabled
              },
              // For instances where the button is supposed to look disabled but is not
              // (e.g. has a popover with an explanation for why it is disabled)
              "&.disabled": {
                pointerEvents: "auto"
              },
              "&:focus, &.focus": {
                outlineWidth: "2px",
                outlineStyle: "solid",
                outlineColor: "transparent"
              },
              "& .MuiCircularProgress-root": {
                color: "inherit"
              }
            }),
            contained: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.common.white,
                borderColor: currentTheme.palette.primary.main,
                backgroundColor: currentTheme.palette.primary.main,
                "&:hover, &:focus, &.hover, &.focus": {
                  borderColor: darken(currentTheme.palette.primary.main, 0.2),
                  backgroundColor: darken(
                    currentTheme.palette.primary.main,
                    0.2
                  )
                },
                "&:focus:not(:focus-visible):not(:hover)": {
                  boxShadow: "none",
                  borderColor: currentTheme.palette.primary.main,
                  backgroundColor: currentTheme.palette.primary.main
                },
                "&:focus-visible, &.focus": {
                  borderColor: currentTheme.palette.common.white,
                  boxShadow: `0 0 0 1px ${currentTheme.palette.common.white}, 0 0 0 3px ${darken(
                    currentTheme.palette.primary.main,
                    0.2
                  )}`
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: darken(
                    currentTheme.palette.primary.main,
                    0.4
                  ),
                  borderColor: darken(currentTheme.palette.primary.main, 0.4)
                }
              },
              "&:disabled, &.disabled": {
                borderColor: currentTheme.palette.common.white,
                backgroundColor: currentTheme.palette.action.disabledBackground,
                boxShadow: "none"
              }
            }),
            text: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.primary.main
              },
              "&:hover, &.hover": {
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.1)
              },
              "&:focus:not(:focus-visible)": {
                outline: "none",
                boxShadow: "none"
              },
              "&:focus-visible:not(:active), &.focus": {
                borderColor: currentTheme.palette.grey[900],
                boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
              },
              "&:active, &.active": {
                boxShadow: "none",
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.2)
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: "transparent",
                boxShadow: "none"
              }
            }),
            outlinedPrimary: ({ theme: currentTheme }) => ({
              borderColor: currentTheme.palette.primary.main,
              backgroundColor: currentTheme.palette.common.white,
              "&:hover, &.hover": {
                backgroundColor: `color-mix(in srgb, ${currentTheme.palette.primary.light} 10%, ${currentTheme.palette.common.white})`
              },
              "&:focus:not(:focus-visible)": {
                outline: "none",
                boxShadow: "none"
              },
              "&:focus-visible, &.focus": {
                borderColor: currentTheme.palette.grey[900],
                boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
              },
              "&:active, &.active": {
                backgroundColor: `color-mix(in srgb, ${currentTheme.palette.primary.light} 20%, ${currentTheme.palette.common.white})`,
                borderColor: currentTheme.palette.primary.dark,
                boxShadow: "none"
              },
              "&:disabled, &.disabled": {
                backgroundColor: currentTheme.palette.common.white,
                borderColor: currentTheme.palette.grey[200],
                color: currentTheme.palette.text.disabled,
                boxShadow: "none"
              }
            }),
            outlined: ({ theme: currentTheme }) => ({
              "&.MuiButton-outlinedNeutral, &.MuiButton-outlinedDefault": {
                borderColor: currentTheme.palette.grey[600],
                backgroundColor: currentTheme.palette.common.white,
                "&:hover, &.hover": {
                  backgroundColor: `color-mix(in srgb, ${currentTheme.palette.grey[500]} 10%, ${currentTheme.palette.common.white})`
                },
                "&:focus:not(:focus-visible)": {
                  outline: "none",
                  boxShadow: "none"
                },
                "&:focus-visible, &.focus": {
                  borderColor: currentTheme.palette.grey[900],
                  boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: `color-mix(in srgb, ${currentTheme.palette.grey[500]} 20%, ${currentTheme.palette.common.white})`,
                  borderColor: currentTheme.palette.grey[900]
                },
                "&:not(:disabled, .disabled)": {
                  color: currentTheme.palette.common.black
                },
                "&:disabled, &.disabled": {
                  boxShadow: "none",
                  backgroundColor: currentTheme.palette.common.white,
                  borderColor: currentTheme.palette.grey[200]
                }
              },
              "&:disabled, &.disabled": {
                backgroundColor: currentTheme.palette.common.white,
                borderColor: currentTheme.palette.grey[200],
                boxShadow: "none"
              }
            })
          }
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
            disableTouchRipple: true
          }
        },
        MuiButtonGroup: {
          defaultProps: {
            disableElevation: true,
            disableFocusRipple: true,
            disableRipple: true,
            color: "default",
            size: "medium",
            variant: "outlined"
          },
          styleOverrides: {
            grouped: ({ theme: currentTheme }) => ({
              backgroundColor: currentTheme.palette.common.white,
              "&.MuiButton-root": {
                "&:focus, &.focus": {
                  boxShadow: `inset 0 0 0 1px ${currentTheme.palette.grey[900]}`,
                  outline: "2px solid transparent",
                  outlineOffset: "-2px"
                },
                "&:disabled, &.disabled": {
                  borderColor: `${currentTheme.palette.grey[400]}`
                }
              }
            }),
            groupedHorizontal: ({ theme: currentTheme }) => ({
              /* Need the border of the next button in the group to match */
              "&:focus, &.focus, &:active, &.active": {
                "& + .MuiButton-root": {
                  borderLeftColor: `${currentTheme.palette.grey[900]}`
                }
              }
            }),
            groupedVertical: ({ theme: currentTheme }) => ({
              /* Need the border of the next button in the group to match */
              "&:focus, &.focus, &:active, &.active": {
                "& + .MuiButton-root": {
                  borderTopColor: `${currentTheme.palette.grey[900]}`
                }
              }
            })
          }
        },
        MuiPickersCalendarHeader: {
          styleOverrides: {
            labelContainer: {
              padding: 2
            }
          }
        },
        MuiPickersDay: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                "&:is(.Mui-focusVisible,:focus,:focus-visible)": {
                  outline: "2px solid",
                  outlineOffset: "-2px",
                  outlineColor: currentTheme.palette.neutral.main,
                  background: "transparent"
                },
                "&:is(:hover, .hover)": {
                  background: currentTheme.palette.action.hover
                },
                "&:is(.Mui-selected)": {
                  backgroundColor: currentTheme.palette.primary.main,
                  "&:is(:hover, .hover)": {
                    background: darken(currentTheme.palette.primary.main, 0.2)
                  }
                }
              }
            }),
            today: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                "&:is(.Mui-selected)": {
                  backgroundColor: currentTheme.palette.primary.main,
                  "&:is(:hover, .hover)": {
                    background: darken(currentTheme.palette.primary.main, 0.2)
                  }
                }
              }
            })
          }
        },
        MuiPickersLayout: {
          styleOverrides: {
            contentWrapper: ({ theme: currentTheme }) => ({
              "& .MuiIconButton-root:not(:disabled, .disabled, .selected)": {
                color: currentTheme.palette.action.active,
                "&:is(:hover,&.hover)": {
                  backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
                }
              }
            })
          }
        },
        MuiPieChart: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontSize: currentTheme.typography.body2.fontSize,
              fontWeight: currentTheme.typography.body2.fontWeight,
              letterSpacing: currentTheme.typography.body2.letterSpacing,
              lineHeight: currentTheme.typography.body2.lineHeight
            })
          }
        },
        MuiCardActionArea: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&:hover": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              "&:focus-visible, &.focus, &:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2),
                outline: "3px solid",
                outlineColor: currentTheme.palette.default.dark,
                outlineOffset: "-3px",
                borderRadius: currentTheme.shape.borderRadius
              }
            })
          }
        },
        MuiCardActions: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiCardHeader: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2),
              // When avatar is present, apply body2 typography to title
              "&:has(.MuiCardHeader-avatar) .MuiCardHeader-title": {
                fontSize: currentTheme.typography.body2.fontSize,
                lineHeight: currentTheme.typography.body2.lineHeight
              }
            }),
            action: () => ({
              margin: 0
            })
          }
        },
        MuiCardContent: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiCardMedia: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiChartsLegend: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontSize: currentTheme.typography.body2.fontSize,
              fontWeight: currentTheme.typography.body2.fontWeight,
              letterSpacing: currentTheme.typography.body2.letterSpacing,
              lineHeight: currentTheme.typography.body2.lineHeight,
              textTransform: "none"
            })
          }
        },
        MuiCheckbox: {
          defaultProps: {
            color: "default"
          },
          styleOverrides: {
            root: ({ ownerState, theme: currentTheme }) => ({
              padding: 12,
              width: "48px",
              height: "48px",
              borderRadius: currentTheme.shape.borderRadius,
              "&:hover": {
                backgroundColor: currentTheme.palette.action.hover,
                backgroundClip: "content-box",
                borderRadius: "16px",
                color: currentTheme.palette.grey["900"]
              },
              "&.Mui-focusVisible .MuiSvgIcon-root": {
                outline: "2px solid transparent",
                outlineOffset: -2,
                borderRadius: "4px",
                outlineColor: currentTheme.palette.text.primary
              },
              "&.Mui-disabled": {
                color: currentTheme.palette.action.disabled
              },
              "&.Mui-error, &.MuiCheckbox-colorError": {
                ...!ownerState.disabled && {
                  color: currentTheme.palette.error.main
                }
              },
              "&.Mui-checked": {
                ...!(ownerState.disabled || ownerState.color === "error") && {
                  color: currentTheme.palette.grey["900"]
                }
              }
            }),
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            })
          }
        },
        MuiChip: {
          defaultProps: {
            variant: "outlined"
          },
          styleOverrides: {
            root: ({ theme: currentTheme, ownerState }) => {
              const colorObj = currentTheme.palette[ownerState.color];
              return {
                position: "relative",
                fontWeight: currentTheme.typography.fontWeightMedium,
                fontSize: currentTheme.typography.subtitle2.fontSize,
                color: currentTheme.palette.text.primary,
                backgroundColor: currentTheme.palette.common.white,
                border: `1px solid ${colorObj.main}`,
                // Increase click/touch target for clickable chips
                "&.MuiChip-clickable:after": {
                  content: '""',
                  position: "absolute",
                  top: "-6px",
                  bottom: "-6px",
                  left: "0",
                  right: "calc(2em + 6px)"
                },
                // Increase click/touch target for delete icon for deletable chips
                "&.MuiChip-deletable .MuiChip-deleteIcon": {
                  padding: "10px 10px 10px 0",
                  margin: "-10px -5px -10px -6px"
                },
                [`& .MuiSvgIcon-root:not(.MuiAvatar-fallback)`]: {
                  color: currentTheme.palette.text.primary
                },
                [`& .MuiChip-avatarColor${(ownerState.color?.charAt(0).toUpperCase() ?? "") + (ownerState.color?.slice(1) ?? "")}`]: {
                  color: colorObj.contrastText,
                  backgroundColor: alpha(colorObj.main, 0.7)
                },
                [`:is(.MuiChip-clickable).MuiChip-color${(ownerState.color?.charAt(0).toUpperCase() ?? "") + (ownerState.color?.slice(1) ?? "")}:is(:hover,.hover)`]: {
                  backgroundColor: `color-mix(in srgb, ${currentTheme.palette.common.black} 4%, ${currentTheme.palette.common.white})`
                },
                [`&:is(.MuiChip-deletable).MuiChip-color${(ownerState.color?.charAt(0).toUpperCase() ?? "") + (ownerState.color?.slice(1) ?? "")} .MuiChip-deleteIcon:is(:hover,.hover)`]: {
                  color: currentTheme.palette.text.primary
                },
                [`&.MuiChip-clickable.MuiChip-color${(ownerState.color?.charAt(0).toUpperCase() ?? "") + (ownerState.color?.slice(1) ?? "")}:is(:active,.active)`]: {
                  boxShadow: currentTheme.shadows[1]
                },
                "&:is(.Mui-focusVisible,:focus-visible)": {
                  outline: "3px solid",
                  outlineColor: currentTheme.palette.neutral.main,
                  outlineOffset: -2,
                  backgroundColor: currentTheme.palette.common.white
                },
                "&.Mui-disabled": {
                  backgroundColor: currentTheme.palette.common.white,
                  borderColor: alpha(colorObj.main, 0.38),
                  color: currentTheme.palette.text.disabled,
                  opacity: 1,
                  "& .MuiSvgIcon-root": {
                    color: currentTheme.palette.text.disabled
                  },
                  "& .MuiChip-deleteIcon": {
                    color: currentTheme.palette.text.disabled
                  }
                }
              };
            }
          }
        },
        MuiCircularProgress: {
          defaultProps: {
            color: "primary",
            variant: "indeterminate"
          }
        },
        MuiFilterWizard: {
          styleOverrides: {
            answer: ({ theme: currentTheme }) => ({
              "&:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.grey[500], 0.2),
                borderColor: currentTheme.palette.grey[900],
                boxShadow: "none"
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: currentTheme.palette.grey[200],
                boxShadow: "none"
              },
              "&:focus:not(:focus-visible)": {
                boxShadow: "none",
                outline: "none"
              },
              "&:focus-visible, &.focus": {
                borderColor: currentTheme.palette.grey[900],
                boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
              },
              "&:hover, &.hover": {
                backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
              },
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.common.black
              },
              border: `1px solid ${currentTheme.palette.grey[600]}`,
              borderRadius: currentTheme.shape.borderRadius,
              padding: currentTheme.spacing(2)
            }),
            answers: ({ theme: currentTheme }) => ({
              display: "flex",
              flexDirection: "column",
              gap: currentTheme.spacing(1)
            }),
            answerText: ({ theme: currentTheme }) => ({
              display: "flex",
              flexDirection: "column",
              gap: currentTheme.spacing(0.5),
              margin: 0,
              p: {
                display: "flex",
                flexDirection: "column",
                gap: currentTheme.spacing(0.5)
              }
            }),
            disclaimer: ({ theme: currentTheme }) => ({
              marginTop: currentTheme.spacing(1)
            }),
            header: ({ theme: currentTheme }) => ({
              fontWeight: currentTheme.typography.fontWeightMedium,
              marginTop: currentTheme.spacing(1)
            }),
            navigation: ({ theme: currentTheme }) => ({
              margin: currentTheme.spacing(1, 0, 5)
            }),
            progress: ({ theme: currentTheme }) => ({
              marginBottom: currentTheme.spacing(1)
            }),
            question: {
              border: "none",
              margin: 0,
              padding: 0
            },
            questionHeader: ({ theme: currentTheme }) => ({
              margin: currentTheme.spacing(2, 0),
              padding: 0
            })
          }
        },
        MuiFormControl: {
          defaultProps: {
            color: "default"
          },
          variants: [
            {
              props: { color: "default" },
              style: {
                color: themeWithPrimitives.palette.default.dark
              }
            }
          ]
        },
        MuiSelect: {
          styleOverrides: {
            select: ({ theme: currentTheme }) => ({
              minWidth: "16rem",
              "&.MuiTablePagination-select": {
                minWidth: "48px",
                padding: currentTheme.spacing(1, 1.5),
                textAlign: "left",
                textAlignLast: "left"
              }
            })
          },
          defaultProps: {
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: "300px"
                }
              }
            }
          }
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: {
              fontSize: "1rem"
            },
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            }),
            error: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.error.main
            }),
            asterisk: ({ ownerState, theme: currentTheme }) => ({
              ...ownerState.disabled && {
                color: currentTheme.palette.action.disabled
              }
            })
          }
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              gap: 4,
              margin: 0,
              marginTop: "2px",
              fontSize: currentTheme.typography.body2.fontSize,
              color: currentTheme.palette.text.secondary,
              textTransform: "none",
              "&.Mui-disabled": {
                color: currentTheme.palette.text.disabled
              },
              "&.Mui-error": {
                color: currentTheme.palette.error.main,
                display: "flex",
                alignItems: "flex-start",
                "& .MuiSvgIcon-root": {
                  width: "16px",
                  height: "16px",
                  marginRight: 4,
                  marginTop: 2
                }
              }
            })
          }
        },
        MuiFormLabel: {
          defaultProps: {
            color: "default"
          },
          variants: [
            {
              props: { color: "default" },
              style: {
                color: themeWithPrimitives.palette.default.dark
              }
            }
          ],
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body1.fontSize,
              marginBottom: "4px",
              fontWeight: 600,
              "&.Mui-focused": {
                color: currentTheme.palette.default.dark
              },
              "&.Mui-disabled": {
                color: currentTheme.palette.text.disabled
              },
              "&.Mui-error": {
                color: currentTheme.palette.error.main
              }
            })
          }
        },
        MuiIconButton: {
          defaultProps: {
            variant: "text",
            color: "neutral",
            size: "medium"
          },
          variants: [
            {
              props: { size: "small" },
              style: ({ theme: currentTheme }) => ({
                padding: currentTheme.spacing(0.25, 0.25),
                minHeight: "32px",
                minWidth: "32px"
              })
            },
            {
              props: { variant: "contained" },
              style: ({ theme: currentTheme }) => ({
                "&:not(:disabled, .disabled)": {
                  color: currentTheme.palette.common.white,
                  borderColor: currentTheme.palette.primary.main,
                  backgroundColor: currentTheme.palette.primary.main,
                  "&:hover, &:focus, &.hover, &.focus": {
                    borderColor: darken(currentTheme.palette.primary.main, 0.2),
                    backgroundColor: darken(
                      currentTheme.palette.primary.main,
                      0.2
                    )
                  },
                  "&:focus:not(:focus-visible):not(:hover)": {
                    boxShadow: "none",
                    borderColor: currentTheme.palette.primary.main,
                    backgroundColor: currentTheme.palette.primary.main
                  },
                  "&:focus-visible, &.focus": {
                    borderColor: currentTheme.palette.common.white,
                    boxShadow: `0 0 0 1px ${currentTheme.palette.common.white}, 0 0 0 3px ${darken(
                      currentTheme.palette.primary.main,
                      0.2
                    )}`
                  },
                  "&:active, &.active": {
                    boxShadow: "none",
                    backgroundColor: darken(
                      currentTheme.palette.primary.main,
                      0.4
                    ),
                    borderColor: darken(currentTheme.palette.primary.main, 0.4)
                  },
                  "&.MuiIconButton-colorError": {
                    "&:not(:disabled, .disabled)": {
                      backgroundColor: currentTheme.palette.error.main,
                      color: currentTheme.palette.error.contrastText,
                      borderColor: currentTheme.palette.error.main,
                      "&:hover, &:focus, &.hover, &.focus": {
                        backgroundColor: darken(
                          currentTheme.palette.error.main,
                          0.2
                        ),
                        borderColor: darken(
                          currentTheme.palette.error.main,
                          0.2
                        )
                      },
                      "&:focus:not(:focus-visible):not(:hover)": {
                        boxShadow: "none",
                        borderColor: currentTheme.palette.error.main,
                        backgroundColor: currentTheme.palette.error.main
                      },
                      "&:focus-visible, &.focus": {
                        borderColor: currentTheme.palette.common.white,
                        boxShadow: `0 0 0 1px ${currentTheme.palette.common.white}, 0 0 0 3px ${darken(
                          currentTheme.palette.error.main,
                          0.2
                        )}`
                      },
                      "&:active, &.active": {
                        boxShadow: "none",
                        backgroundColor: darken(
                          currentTheme.palette.error.main,
                          0.4
                        ),
                        borderColor: darken(
                          currentTheme.palette.error.main,
                          0.4
                        )
                      }
                    }
                  }
                },
                "&:disabled, &.disabled": {
                  borderColor: currentTheme.palette.common.white,
                  backgroundColor: currentTheme.palette.action.disabledBackground,
                  boxShadow: "none"
                }
              })
            },
            {
              props: { variant: "text" },
              style: ({ theme: currentTheme }) => ({
                "&:not(:disabled, .disabled)": {
                  color: currentTheme.palette.primary.main
                },
                "&:hover, &.hover": {
                  backgroundColor: alpha(
                    currentTheme.palette.primary.light,
                    0.1
                  )
                },
                "&:focus:not(:focus-visible)": {
                  outline: "none",
                  boxShadow: "none"
                },
                "&:focus-visible:not(:active), &.focus": {
                  borderColor: currentTheme.palette.grey[900],
                  boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: alpha(
                    currentTheme.palette.primary.light,
                    0.2
                  )
                },
                "&:disabled, &.disabled": {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  boxShadow: "none"
                },
                "&.MuiIconButton-colorNeutral": {
                  "&:hover, &.hover": {
                    backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
                  },
                  "&:focus:not(:focus-visible)": {
                    outline: "none",
                    boxShadow: "none"
                  },
                  "&:focus-visible, &.focus": {
                    borderColor: currentTheme.palette.grey[900],
                    boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                  },
                  "&:active, &.active": {
                    boxShadow: "none",
                    backgroundColor: alpha(currentTheme.palette.grey[500], 0.2)
                  },
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.common.black
                  },
                  "&:disabled, &.disabled": {
                    boxShadow: "none",
                    backgroundColor: "transparent"
                  }
                },
                "&.MuiIconButton-colorError": {
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.error.main,
                    "&:hover, &.hover": {
                      backgroundColor: alpha(
                        currentTheme.palette.error.main,
                        0.1
                      )
                    },
                    "&:focus:not(:focus-visible)": {
                      outline: "none",
                      boxShadow: "none"
                    },
                    "&:focus-visible:not(:active), &.focus": {
                      borderColor: currentTheme.palette.grey[900],
                      boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                    },
                    "&:active, &.active": {
                      boxShadow: "none",
                      backgroundColor: alpha(
                        currentTheme.palette.error.main,
                        0.2
                      )
                    }
                  }
                }
              })
            },
            {
              props: { variant: "outlined" },
              style: ({ theme: currentTheme }) => ({
                borderColor: currentTheme.palette.primary.main,
                backgroundColor: currentTheme.palette.common.white,
                "&:not(:disabled, .disabled)": {
                  color: currentTheme.palette.primary.main
                },
                "&:hover, &.hover": {
                  backgroundColor: `color-mix(in srgb, ${currentTheme.palette.primary.light} 10%, ${currentTheme.palette.common.white})`
                },
                "&:focus:not(:focus-visible)": {
                  outline: "none",
                  boxShadow: "none"
                },
                "&:focus-visible, &.focus": {
                  borderColor: currentTheme.palette.grey[900],
                  boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                },
                "&:active, &.active": {
                  backgroundColor: `color-mix(in srgb, ${currentTheme.palette.primary.light} 20%, ${currentTheme.palette.common.white})`,
                  borderColor: currentTheme.palette.primary.dark,
                  boxShadow: "none"
                },
                "&:disabled, &.disabled": {
                  backgroundColor: currentTheme.palette.common.white,
                  color: currentTheme.palette.text.disabled,
                  borderColor: currentTheme.palette.grey[200],
                  boxShadow: "none"
                },
                "&.MuiIconButton-colorNeutral": {
                  borderColor: currentTheme.palette.grey[600],
                  "&:hover, &.hover": {
                    backgroundColor: `color-mix(in srgb, ${currentTheme.palette.grey[500]} 10%, ${currentTheme.palette.common.white})`
                  },
                  "&:focus:not(:focus-visible)": {
                    outline: "none",
                    boxShadow: "none"
                  },
                  "&:focus-visible, &.focus": {
                    borderColor: currentTheme.palette.grey[900],
                    boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                  },
                  "&:active, &.active": {
                    boxShadow: "none",
                    backgroundColor: `color-mix(in srgb, ${currentTheme.palette.grey[500]} 20%, ${currentTheme.palette.common.white})`,
                    borderColor: currentTheme.palette.grey[900]
                  },
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.common.black
                  },
                  "&:disabled, &.disabled": {
                    boxShadow: "none",
                    borderColor: currentTheme.palette.grey[200],
                    backgroundColor: currentTheme.palette.common.white,
                    color: currentTheme.palette.text.disabled
                  }
                },
                "&.MuiIconButton-colorError": {
                  "&:not(:disabled, .disabled)": {
                    color: currentTheme.palette.error.main,
                    borderColor: currentTheme.palette.error.main,
                    "&:hover, &.hover": {
                      backgroundColor: `color-mix(in srgb, ${currentTheme.palette.error.main} 10%, ${currentTheme.palette.common.white})`
                    },
                    "&:focus:not(:focus-visible)": {
                      outline: "none",
                      boxShadow: "none"
                    },
                    "&:focus-visible, &.focus": {
                      borderColor: currentTheme.palette.grey[900],
                      boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
                    },
                    "&:active, &.active": {
                      backgroundColor: `color-mix(in srgb, ${currentTheme.palette.error.main} 20%, ${currentTheme.palette.common.white})`,
                      borderColor: currentTheme.palette.error.dark,
                      boxShadow: "none"
                    }
                  }
                }
              })
            }
          ],
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              position: "relative",
              borderRadius: currentTheme.shape.borderRadius,
              borderColor: "transparent",
              borderStyle: "solid",
              borderWidth: "1px",
              padding: currentTheme.spacing(1, 1),
              minHeight: "44px",
              minWidth: "44px",
              fontSize: "1.5rem",
              boxSizing: "border-box",
              transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              "&:not(:disabled, .disabled)": {
                color: "inherit"
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: "transparent",
                boxShadow: "none"
              },
              "&:after": {
                content: '""',
                position: "absolute",
                top: currentTheme.spacing(-0.5),
                bottom: currentTheme.spacing(-0.5),
                left: currentTheme.spacing(-0.5),
                right: currentTheme.spacing(-0.5)
              }
            })
          }
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "& .MuiIconButton-root:not(:disabled, .disabled)": {
                color: currentTheme.palette.action.active,
                "&:hover, &:focus, &.hover, &.focus": {
                  backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
                }
              }
            })
          }
        },
        MuiInputBase: {
          defaultProps: {
            color: "default"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(0),
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: currentTheme.palette.grey[600],
              borderRadius: currentTheme.shape.borderRadius,
              backgroundColor: currentTheme.palette.common.white,
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none"
              },
              "&.MuiInputBase-adornedStart": {
                paddingLeft: currentTheme.spacing(1.5)
              },
              "&.MuiInputBase-adornedEnd": {
                paddingRight: currentTheme.spacing(1.5)
              },
              "&:hover:not(.Mui-disabled)": {
                borderColor: currentTheme.palette.default.dark,
                "&.Mui-error": {
                  borderColor: currentTheme.palette.error.main
                }
              },
              "&:focus-within, &.Mui-focused": {
                outline: "3px solid",
                outlineColor: currentTheme.palette.neutral.main,
                outlineOffset: -2,
                "&.Mui-error": {
                  borderColor: currentTheme.palette.error.main,
                  outlineColor: currentTheme.palette.neutral.main
                }
              },
              "&.Mui-error": {
                border: "2px solid",
                borderColor: currentTheme.palette.error.main,
                "&.Mui-disabled": {
                  border: "1px solid",
                  borderColor: currentTheme.palette.action.disabled
                }
              },
              "&.Mui-disabled": {
                borderColor: currentTheme.palette.action.disabled,
                color: currentTheme.palette.text.disabled
              },
              "& input::placeholder, & textarea::placeholder": {
                color: currentTheme.palette.grey[600],
                opacity: 1
              }
            }),
            input: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2, 1.5, 2, 1.5),
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body2.lineHeight,
              "&.MuiInputBase-inputSizeSmall": {
                padding: currentTheme.spacing(1, 1.5, 1, 1.5)
              }
            })
          }
        },
        MuiInputLabel: {
          defaultProps: {
            color: "default",
            variant: "outlined"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontSize: 18,
              fontWeight: currentTheme.typography.fontWeightBold
            })
          }
        },
        MuiLinearProgress: {
          defaultProps: {
            color: "primary",
            variant: "indeterminate"
          },
          styleOverrides: {
            root: {
              width: "100%"
            },
            label: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body2.lineHeight
            })
          }
        },
        MuiLink: {
          defaultProps: {
            color: "info.main",
            tabIndex: 0,
            underline: "always"
          },
          styleOverrides: {
            root: ({ theme: currentTheme, ownerState }) => ({
              textDecoration: "underline",
              textDecorationColor: "currentcolor",
              textUnderlinePosition: "from-font",
              // Increase underline thickness on hover
              "&:hover": {
                textDecorationThickness: "15%"
              },
              // Visited links styling - excludes breadcrumb links
              "&:visited:not(.MuiBreadcrumbs-root .MuiLink-root)": {
                color: ownerState.color === "inherit" ? `var(--link-visited-color, color-mix(in srgb, ${currentTheme.palette.common.black} 60%, ${currentTheme.palette.background.default} 40%))` : currentTheme.palette.info.dark
              },
              /**
               * Browsers handle the focus outline differently, even across different operating systems.
               * For instance, Firefox and Safari on MacOS will not even allow tabbing to links without explicitly
               * enabling the setting in the browser or the OS. Once enabled, the focus outline is controlled by
               * the user agent styles.
               * The overall size of the outline is largely driven by the font-size and line-height (whichever
               * is larger).
               * The outline-offset below ensures that there is at least a 2px gap between the outline and the
               * text.
               */
              "&:focus-visible, &.focus, &:active, &.active": {
                textDecoration: "none",
                outline: "2px solid",
                outlineColor: currentTheme.palette.default.dark,
                outlineOffset: "2px",
                borderRadius: "2px"
              }
            })
          }
        },
        MuiMenu: {
          styleOverrides: {
            root: {
              ".MuiBackdrop-root": {
                backgroundColor: "transparent"
              }
            }
          }
        },
        MuiMenuItem: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body2.fontSize,
              lineHeight: currentTheme.typography.body2.lineHeight,
              "&:hover": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              "&:active, &.active, &:focus-visible, &.focus": {
                outline: "2px solid",
                outlineColor: currentTheme.palette.neutral.main,
                outlineOffset: "-2px",
                borderRadius: currentTheme.shape.borderRadius
              },
              "&:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              "&:focus-visible, &.focus": {
                backgroundColor: "transparent"
              },
              "&.Mui-selected": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4),
                fontWeight: currentTheme.typography.fontWeightInlineBold,
                // Combat Mui re-applying primary to these states.
                "&:hover": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.4),
                  fontWeight: currentTheme.typography.fontWeightInlineBold
                },
                "&:focus, &:focus-visible, &.Mui-focusVisible": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.4),
                  outline: "2px solid",
                  outlineColor: currentTheme.palette.neutral.main,
                  outlineOffset: "-2px",
                  borderRadius: currentTheme.shape.borderRadius,
                  fontWeight: currentTheme.typography.fontWeightInlineBold
                }
              }
            })
          }
        },
        MuiMobileStepper: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(1, 0)
            })
          }
        },
        MuiPagination: {
          defaultProps: {
            color: "standard",
            size: "large"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "& .MuiPaginationItem-root": {
                "&:focus-visible, &.Mui-focusVisible": {
                  outline: "2px solid",
                  outlineColor: currentTheme.palette.neutral.main,
                  outlineOffset: "-2px",
                  backgroundColor: "transparent"
                },
                "&:hover:focus-visible, &:hover.Mui-focusVisible": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
                }
              }
            })
          }
        },
        MuiPaginationItem: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&.Mui-selected:not(.MuiPaginationItem-colorPrimary)": {
                backgroundColor: currentTheme.palette.grey[900],
                color: currentTheme.palette.common.white,
                "&:focus-visible, &.Mui-focusVisible": {
                  backgroundColor: darken(currentTheme.palette.grey[900], 0.2)
                },
                "&:hover": {
                  backgroundColor: darken(currentTheme.palette.grey[900], 0.1)
                },
                "&:hover:focus-visible, &:hover.Mui-focusVisible": {
                  backgroundColor: darken(currentTheme.palette.grey[900], 0.2)
                }
              },
              "&.Mui-selected.MuiPaginationItem-colorPrimary": {
                backgroundColor: currentTheme.palette.primary.main,
                color: currentTheme.palette.primary.contrastText,
                "&:focus-visible, &.Mui-focusVisible": {
                  backgroundColor: darken(
                    currentTheme.palette.primary.main,
                    0.2
                  )
                },
                "&:hover": {
                  backgroundColor: darken(
                    currentTheme.palette.primary.main,
                    0.1
                  )
                },
                "&:hover:focus-visible, &:hover.Mui-focusVisible": {
                  backgroundColor: darken(
                    currentTheme.palette.primary.main,
                    0.2
                  )
                }
              }
            })
          }
        },
        MuiRadio: {
          defaultProps: { color: "default" },
          styleOverrides: {
            root: ({ ownerState, theme: currentTheme }) => ({
              borderRadius: currentTheme.shape.borderRadius,
              width: "48px",
              height: "48px",
              "&:hover": {
                backgroundColor: currentTheme.palette.action.hover,
                backgroundClip: "content-box",
                borderRadius: "16px",
                color: currentTheme.palette.text.primary
              },
              "&.MuiRadio-colorError": {
                color: currentTheme.palette.error.main
              },
              "&.Mui-focusVisible .MuiSvgIcon-root:first-of-type": {
                outlineWidth: "2px",
                outlineOffset: "1px",
                outlineStyle: "solid",
                borderRadius: "4px",
                outlineColor: currentTheme.palette.text.primary
              },
              "&.Mui-disabled": {
                color: currentTheme.palette.action.disabled
              },
              "&.Mui-checked": {
                ...!(ownerState.disabled || ownerState.color === "error") && {
                  color: currentTheme.palette.text.primary
                }
              }
            })
          }
        },
        MuiSnackbar: {
          styleOverrides: {
            root: () => ({
              // UX does not want the snackbars to be difference sizes based on whether or no they have a button.
              // Set the min height to to account for a button
              minHeight: "48px"
            })
          }
        },
        MuiSnackbarContent: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              paddingTop: currentTheme.spacing(0.25),
              paddingBottom: currentTheme.spacing(0.25),
              backgroundColor: currentTheme.palette.grey[900]
            }),
            message: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body2.fontSize
            }),
            action: () => ({
              "& .MuiButton-root, & .MuiIconButton-root": {
                "&:not(:disabled, .disabled)": {
                  color: "inherit"
                },
                "&:hover, &.hover": {
                  backgroundColor: "color-mix(in srgb, currentColor 10%, transparent)",
                  borderColor: "color-mix(in srgb, currentColor 10%, transparent)"
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)",
                  borderColor: "currentColor"
                },
                "&:focus-visible:not(:active), &.focus": {
                  borderColor: "currentColor",
                  boxShadow: `0 0 0 1px currentColor`
                },
                "&:disabled, &.disabled": {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  boxShadow: "none"
                }
              }
            })
          }
        },
        MuiStep: {
          styleOverrides: {
            root: {
              "&.MuiStep-vertical.MuiStep-alternativeLabel": {
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }
            }
          }
        },
        MuiStepLabel: {
          styleOverrides: {
            label: ({ theme: currentTheme }) => ({
              "&.Mui-active,&.Mui-completed": {
                fontWeight: currentTheme.typography.fontWeightMedium
              }
            })
          }
        },
        MuiStepConnector: {
          styleOverrides: {
            root: {
              "&.MuiStepConnector-vertical.MuiStepConnector-alternativeLabel": {
                marginLeft: "initial",
                position: "initial",
                top: "unset",
                left: "unset",
                right: "unset"
              }
            }
          }
        },
        MuiStepper: {
          styleOverrides: {
            root: {
              "&.MuiStepper-vertical.MuiStepper-alternativeLabel": {
                alignItems: "center",
                width: "fit-content"
              }
            }
          }
        },
        MuiSvgIcon: {
          variants: [
            {
              props: { fontSize: "inherit" },
              style: {
                fontSize: "inherit"
              }
            },
            {
              props: { fontSize: "small" },
              style: {
                fontSize: "1.25rem"
              }
            },
            {
              props: { fontSize: "medium" },
              style: {
                fontSize: "1.5rem"
              }
            },
            {
              props: { fontSize: "large" },
              style: {
                fontSize: "2rem"
              }
            },
            {
              props: { fontSize: "xlarge" },
              style: {
                fontSize: "3rem"
              }
            }
          ]
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              "&:has(.MuiCheckbox-root)": {
                padding: 4.75
              }
            },
            head: ({ theme: currentTheme }) => ({
              fontWeight: currentTheme.typography.fontWeightMedium,
              verticalAlign: "bottom"
            }),
            body: {
              verticalAlign: "top"
            }
          }
        },
        MuiTableRow: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&:has(.Mui-checked)": {
                background: currentTheme.palette.grey["100"]
              },
              "&.MuiTableRow-hover": {
                "&:hover": {
                  background: alpha(currentTheme.palette.grey[400], 0.2)
                }
              }
            })
          }
        },
        MuiTableSortLabel: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.h6.fontFamily,
              fontWeight: currentTheme.typography.h6.fontWeight,
              fontSize: currentTheme.typography.h6.fontSize,
              lineHeight: currentTheme.typography.h6.lineHeight,
              alignItems: "center",
              "&:hover": {
                color: "inherit"
              },
              "&:not(.Mui-active):is(:focus-visible,.Mui-focusVisible) .MuiTableSortLabel-icon": {
                opacity: 0.5
              },
              "&:is(:focus-visible, .Mui-focusVisible)": {
                outline: "2px solid",
                outlineColor: currentTheme.palette.default.dark,
                borderRadius: currentTheme.shape.borderRadius,
                backgroundColor: "transparent",
                color: "inherit",
                padding: currentTheme.spacing(0, 0.25),
                opacity: 1
              }
            }),
            icon: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.text.secondary
            })
          }
        },
        MuiTablePagination: {
          defaultProps: {
            showFirstButton: true,
            showLastButton: true,
            slotProps: {
              select: {
                size: "small"
              },
              actions: {
                firstButton: {
                  color: "neutral"
                },
                nextButton: {
                  color: "neutral"
                },
                previousButton: {
                  color: "neutral"
                },
                lastButton: {
                  color: "neutral"
                }
              }
            }
          },
          styleOverrides: {
            input: {
              marginRight: 0
            },
            actions: {
              marginLeft: 0
            },
            displayedRows: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.subtitle2.fontSize,
              margin: currentTheme.spacing(0, 3.25)
            }),
            selectLabel: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.subtitle2.fontSize,
              color: currentTheme.palette.text.secondary
            }),
            select: ({ theme: currentTheme }) => ({
              lineHeight: currentTheme.typography.subtitle1.lineHeight,
              fontSize: currentTheme.typography.subtitle1.fontSize
            }),
            selectIcon: ({ theme: currentTheme }) => ({
              marginRight: currentTheme.spacing(1.5)
            })
          }
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              textDecoration: "none",
              textTransform: "none",
              "&:focus-visible, &.focus, &:active, &.active": {
                outline: "2px solid",
                outlineColor: currentTheme.palette.default.dark,
                outlineOffset: "-0.5rem",
                borderRadius: "10px"
              }
            })
          }
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              ".MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3
              }
            }
          }
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body2.fontSize,
              fontWeight: currentTheme.typography.fontWeightRegular,
              maxWidth: "320px",
              whiteSpace: "normal",
              wordWrap: "break-word"
            })
          }
        },
        MuiTypography: {
          defaultProps: {
            variant: "body2"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "& b, & strong": {
                fontWeight: currentTheme.typography.fontWeightInlineBold
              },
              "& .fontWeightInlineBold": {
                fontWeight: currentTheme.typography.fontWeightInlineBold
              }
            })
          }
        },
        MuiRating: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "& .MuiRating-iconFilled": {
                color: "#EFB23D"
              },
              "&.Mui-focusVisible": {
                "& .MuiRating-iconActive": {
                  borderRadius: currentTheme.shape.borderRadius,
                  outline: "2px solid",
                  outlineOffset: -2,
                  outlineColor: currentTheme.palette.text.primary
                }
              },
              "& .MuiRating-labelEmptyValueActive": {
                borderRadius: currentTheme.shape.borderRadius,
                outline: "2px solid",
                outlineColor: currentTheme.palette.text.primary
              }
            })
          }
        },
        MuiListItemText: {
          defaultProps: {
            primaryTypographyProps: { variant: "body2" }
          },
          styleOverrides: {
            secondary: {
              fontSize: "1rem"
            }
          }
        },
        MuiList: {
          styleOverrides: {
            root: {
              "& .MuiListItem-root": {
                paddingTop: 0,
                paddingBottom: 0
              }
            }
          }
        },
        MuiListItemButton: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&:active, &.active, &:focus-visible, &.focus": {
                outline: "2px solid",
                outlineColor: currentTheme.palette.default.dark,
                outlineOffset: "-2px",
                borderRadius: currentTheme.shape.borderRadius
              },
              "&:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              "&:focus-visible, &.focus": {
                backgroundColor: "transparent"
              },
              "&.Mui-selected": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4),
                "& .MuiListItemText-primary": {
                  fontWeight: currentTheme.typography.fontWeightBold
                },
                "& .MuiListItemText-secondary": {
                  fontWeight: currentTheme.typography.fontWeightBold
                }
              },
              "&:hover": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2),
                "&.Mui-selected": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.4)
                }
              }
            })
          }
        },
        MuiSlider: {
          styleOverrides: {
            thumb: ({ theme: currentTheme }) => ({
              boxShadow: currentTheme.shadows[3]
            })
          }
        },
        MuiDialog: {
          defaultProps: {
            PaperProps: {
              elevation: 12
            }
          }
        },
        MuiDialogTitle: {
          defaultProps: {
            variant: "h4"
          }
        },
        MuiDialogActions: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2, 3, 2, 3)
            })
          }
        },
        MuiDrawer: {
          defaultProps: {
            PaperProps: {
              elevation: 12
            }
          }
        },
        MuiSwitch: {
          defaultProps: {
            color: "default"
          },
          styleOverrides: {
            switchBase: ({ theme: currentTheme, ownerState }) => ({
              "&.MuiSwitch-colorDefault:hover": {
                backgroundColor: currentTheme.palette.action.hover
              },
              "&.MuiSwitch-colorDefault:is(:focus-visible, .Mui-focusVisible),&.MuiSwitch-colorError:is(:focus-visible, .Mui-focusVisible)": {
                borderRadius: "100px",
                outline: `2px solid`,
                outlineColor: theme.palette.neutral.main,
                outlineOffset: "-2px",
                backgroundColor: ownerState.color === "error" && ownerState.checked ? alpha(currentTheme.palette.error.main, 0.12) : alpha(currentTheme.palette.text.primary, 0.12)
              }
            }),
            thumb: ({ theme: currentTheme }) => ({
              ".MuiSwitch-colorDefault.Mui-checked &": {
                backgroundColor: currentTheme.palette.grey[800]
              }
            }),
            track: ({ theme: currentTheme }) => ({
              backgroundColor: currentTheme.palette.grey[900],
              ".Mui-checked + &.MuiSwitch-track": {
                backgroundColor: currentTheme.palette.grey[900]
              }
            })
          }
        },
        MuiBreadcrumbs: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              // Always apply small size to icons in breadcrumb links
              ".MuiLink-root > .MuiSvgIcon-root, .MuiLink-root > svg": {
                fontSize: currentTheme.typography.pxToRem(20),
                verticalAlign: "middle"
              },
              // Default breadcrumb link: text.secondary for text and icon, no underline and margin between icon and text
              ".MuiLink-root": {
                color: currentTheme.palette.text.secondary,
                textDecoration: "none",
                gap: currentTheme.spacing(1),
                "&:hover": {
                  textDecoration: "underline",
                  textDecorationColor: "currentColor"
                },
                fontSize: currentTheme.typography.body2.fontSize
              },
              // Style for Typography components in breadcrumbs (non-link text)
              ".MuiTypography-root": {
                fontSize: currentTheme.typography.body2.fontSize
              },
              // Last breadcrumb: text.primary for text and icon
              ".MuiBreadcrumbs-li:last-child ": {
                color: currentTheme.palette.text.primary,
                "& .MuiLink-root": {
                  color: currentTheme.palette.text.primary
                }
              }
            })
          }
        },
        MuiImage: {
          defaultProps: {
            variant: "square",
            size: "medium"
          },
          styleOverrides: {
            root: ({
              theme: currentTheme,
              ownerState
            }) => ({
              borderRadius: ownerState.variant === "rounded" ? currentTheme.shape.borderRadius : 0,
              objectFit: "cover",
              display: "block",
              maxWidth: "100%"
            })
          },
          variants: [
            {
              props: { size: "small" },
              style: ({
                ownerState
              }) => ({
                height: ownerState.height || 160
              })
            },
            {
              props: { size: "medium" },
              style: ({
                ownerState
              }) => ({
                height: ownerState.height || 240
              })
            },
            {
              props: { size: "large" },
              style: ({
                ownerState
              }) => ({
                height: ownerState.height || 320
              })
            }
          ]
        },
        MuiDataGrid: {
          defaultProps: {
            variant: "default",
            bordered: false
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              // Additional root-level customizations
              // Remove elevation from Paper components inside DataGrid
              "& .MuiPaper-root": {
                boxShadow: "none",
                backgroundImage: "none",
                backgroundColor: currentTheme.palette.common.white,
                transition: "none"
              },
              // MRT Theme styles
              "& .MuiTableHead-root .MuiTableCell-root": {
                fontWeight: currentTheme.typography.fontWeightMedium,
                verticalAlign: "bottom",
                "&.MuiTableCell-root": {
                  fontSize: currentTheme.typography.subtitle2.fontSize
                },
                ".Mui-TableHeadCell-Content": {
                  alignItems: "flex-end"
                },
                ".Mui-TableHeadCell-Content-Labels": {
                  gap: currentTheme.spacing(0.5),
                  alignItems: "flex-end",
                  "& > span.MuiBox-root": {
                    height: "28px",
                    display: "flex",
                    alignItems: "center"
                  }
                },
                ".MuiTableSortLabel-root": {
                  minHeight: "28px",
                  minWidth: "28px",
                  "& .MuiTableSortLabel-icon": {
                    margin: "auto"
                  },
                  "&:is(:focus-visible, .Mui-focusVisible)": {
                    padding: 0,
                    outline: "none",
                    border: `2px solid ${currentTheme.palette.grey[900]}`
                  }
                },
                ".Mui-TableHeadCell-Content-Actions": {
                  "& .MuiIconButton-root": {
                    width: "28px",
                    height: "28px",
                    minHeight: "28px",
                    minWidth: "28px",
                    "& .MuiSvgIcon-root": {
                      width: "18px",
                      height: "18px"
                    },
                    "&:focus-visible, &.Mui-focusVisible": {
                      opacity: 1
                    }
                  }
                }
              },
              // Top toolbar styles
              "& .MuiPaper-root > .MuiBox-root:first-of-type": {
                minHeight: "60px",
                "& .Mui-ToolbarDropZone + .MuiBox-root": {
                  "@media (min-width: 1025px) and (max-width: 1048px)": {
                    position: "relative"
                  }
                },
                "& .MuiBox-root > span + .MuiBox-root": {
                  alignItems: "normal",
                  '& .MuiButton-root[aria-label="Clear all filters"]': {
                    marginLeft: currentTheme.spacing(1)
                  }
                },
                ".MuiFormControl-root": {
                  maxWidth: "200px",
                  height: "100%"
                },
                "& .MuiTextField-root .MuiInputBase-root": {
                  height: "100%",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none"
                  }
                }
              },
              // Bottom toolbar styles
              "& .MuiPaper-root > .MuiBox-root:last-of-type": {
                fontSize: currentTheme.typography.body1.fontSize,
                maxHeight: "60px",
                padding: currentTheme.spacing(1, 0.25),
                boxSizing: "border-box",
                "& .MuiBox-root": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "44px",
                  padding: 0,
                  position: "relative"
                },
                ".MuiTablePagination-root": {
                  padding: 0,
                  gap: currentTheme.spacing(3.25),
                  ".MuiInputLabel-root, .MuiTypography-root": {
                    fontSize: currentTheme.typography.subtitle2.fontSize
                  },
                  ".MuiFormLabel-root.MuiInputLabel-root": {
                    fontSize: currentTheme.typography.subtitle2.fontSize,
                    fontWeight: currentTheme.typography.fontWeightRegular,
                    color: currentTheme.palette.text.secondary,
                    alignSelf: "center",
                    transform: "none"
                  },
                  ".MuiSelect-select": {
                    minWidth: "48px",
                    padding: currentTheme.spacing(1, 1.5),
                    textAlign: "left",
                    textAlignLast: "left"
                  },
                  ".MuiSelect-icon": {
                    marginRight: currentTheme.spacing(1.5)
                  },
                  // DataGrid pagination buttons should be medium size
                  "& .MuiIconButton-root": {
                    padding: currentTheme.spacing(1),
                    minHeight: "44px",
                    minWidth: "44px"
                  },
                  // Add colon to "Rows per page" label
                  'label[for="mrt-rows-per-page"]::after': {
                    content: '":"'
                  }
                }
              },
              // Filter text field styles
              "& .MuiTextField-root": {
                "& .MuiInputBase-root.MuiAutocomplete-inputRoot": {
                  paddingBottom: "0",
                  paddingTop: "0"
                },
                ".MuiInputBase-root": {
                  "& .MuiInput-input": {
                    padding: currentTheme.spacing(1, 1.5, 1, 1.5)
                    // 8px 12px 8px 12px
                  },
                  "& .MuiAutocomplete-input": {
                    padding: currentTheme.spacing(1, 1.5, 1, 1.5),
                    margin: 0
                  },
                  "&::before": {
                    border: "none",
                    borderBottom: "none"
                  },
                  "&:hover:not(.Mui-disabled, .Mui-error)::before": {
                    border: "none",
                    borderBottom: "none"
                  },
                  "&::after": {
                    border: "none",
                    borderBottom: "none"
                  },
                  "&:hover:not(.Mui-disabled, .Mui-error)::after": {
                    border: "none",
                    borderBottom: "none"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none"
                  }
                }
              },
              "& .MuiAutocomplete-root": {
                minWidth: "auto",
                width: "calc(100% + 4px)",
                "& .MuiInputBase-root.MuiAutocomplete-inputRoot": {
                  // Prevent chips from wrapping to new lines only when not focused
                  "&:not(.Mui-focused)": {
                    flexWrap: "nowrap",
                    alignItems: "center",
                    "& .MuiAutocomplete-tag": {
                      // Ensure chips don't force line wrapping
                      flexShrink: 1,
                      minWidth: 0,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }
                    }
                  }
                },
                "& .MuiInputBase-root.MuiAutocomplete-inputRoot.MuiInputBase-adornedEnd": {
                  paddingRight: currentTheme.spacing(9)
                },
                "& .MuiAutocomplete-clearIndicator": {
                  padding: currentTheme.spacing(0.25, 0.25),
                  minWidth: currentTheme.spacing(4),
                  minHeight: currentTheme.spacing(4)
                }
              },
              // Fix IconButton color in filter inputs to use action.active instead of black
              "& .MuiInputAdornment-root .MuiIconButton-root:not(:disabled)": {
                color: currentTheme.palette.action.active
              },
              // Table body styles
              "& .MuiTableBody-root": {
                fontSize: currentTheme.typography.subtitle2.fontSize,
                ".MuiTypography-root": {
                  fontSize: currentTheme.typography.subtitle2.fontSize
                }
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                hover: false,
                fontSize: currentTheme.typography.subtitle2.fontSize
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                fontSize: currentTheme.typography.subtitle2.fontSize,
                verticalAlign: "top"
              },
              // Linear progress styles - show only first occurrence
              "& .MuiLinearProgress-root": {
                display: "block"
              },
              "& .MuiBox-root:last-child .MuiLinearProgress-root": {
                display: "none"
              },
              // Row hover styles
              "& .MuiTableRow-root td:after": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4)
              },
              "& .MuiTableRow-root:not(.Mui-selected):hover td:after": {
                background: "none"
              },
              "& .MuiTableRow-root.Mui-selected:hover td:after": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4)
              },
              "& .MuiTableRow-root.Mui-selected": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4)
              },
              // Toolbar alert banner styles
              "& .MuiAlert-root": {
                backgroundColor: alpha(currentTheme.palette.primary.main, 0.1)
              },
              ".MuiButtonBase-root.MuiIconButton-root": {
                color: currentTheme.palette.text.primary
              },
              ".selected-row-count": {
                color: currentTheme.palette.text.primary,
                fontSize: currentTheme.typography.subtitle1.fontSize,
                fontWeight: currentTheme.typography.fontWeightMedium
              }
            })
          }
        },
        MuiMessageCard: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              display: "grid",
              overflow: "hidden",
              borderRadius: currentTheme.shape.borderRadius,
              padding: currentTheme.spacing(2),
              // Mobile layout (CardHeader to the left of CardMedia)
              gridTemplateAreas: `
                "CardHeader CardMedia"
                "CardContent CardContent"
                "CardActions CardActions"
              `,
              gridTemplateRows: "auto 1fr auto",
              gridTemplateColumns: "1fr auto",
              gap: currentTheme.spacing(2),
              "& .MuiCardHeader-root": {
                gridArea: "CardHeader",
                alignSelf: "start",
                padding: 0
              },
              "& .MuiCardMedia-root": {
                gridArea: "CardMedia",
                alignSelf: "stretch",
                boxSizing: "border-box",
                padding: 0,
                // Mobile
                width: 72,
                height: 72,
                borderRadius: "50%",
                overflow: "hidden",
                // Ensure child elements (img, video, etc.) respect the container bounds
                "& > *": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block"
                }
              },
              "& .MuiCardContent-root": {
                gridArea: "CardContent",
                alignSelf: "start",
                padding: 0,
                flex: 1
              },
              "& .MuiCardActions-root": {
                gridArea: "CardActions",
                alignSelf: "start",
                justifySelf: "start",
                display: "flex",
                flexDirection: "column",
                padding: 0,
                gap: currentTheme.spacing(1),
                width: "100%",
                "& > *": {
                  margin: 0,
                  width: "100%"
                }
              },
              // larger viewport size layout (horizontal)
              [`@container (min-width: ${currentTheme.breakpoints.values.messageCard}px)`]: {
                gridTemplateAreas: `
                    "CardMedia CardHeader CardActions"
                    "CardMedia CardContent CardActions"
                  `,
                gridTemplateRows: "auto 1fr",
                gridTemplateColumns: "128px 1fr fit-content",
                gap: currentTheme.spacing(3),
                "& .MuiCardMedia-root": {
                  width: 128,
                  height: 128,
                  borderRadius: 0
                }
              }
            })
          }
        }
      }
    },
    themeWithPrimitives
  )
);
export {
  theme
};
